import React, { useEffect, useState } from 'react';
import * as S from './DeleteLog.styled';
import EscapeButton from 'pages/components/EscapeButton/EscapeButton';
import Loader from 'pages/components/Loader/Loader';
import InfoOutlined from '@mui/icons-material/InfoOutlined';
import HighlightOff from '@mui/icons-material/HighlightOff';
import { useHistory } from 'react-router-dom';
import { useLogs } from '../hooks';
import { selectLogsChecked, setLogsChecked } from '../redux';
import { useAppDispatch, useAppSelector } from 'redux/hooks';
import { toast } from 'utils/toast';
import { logButtonClick } from 'utils/analytics';
import { ERP_PATHS } from 'pages/Layout/erpModulePaths';
import {
  closeReviewedManagerReport,
  CloseReviewedReportPreview,
  fetchCloseReviewedReportPreview,
} from 'pages/Reporting/redux';
import { reportingPeekNavItem } from 'pages/Reporting/reportingNavStorage';
import { reportContextStandingLabel } from 'pages/Reporting/reportContextUsers';
import { Log } from '../hooks';

const CloseReviewedReport = ({
  onExit,
  activityLogId,
  reportId,
  hideViewReport,
}: {
  onExit: Function,
  activityLogId?: string,
  reportId?: string,
  hideViewReport?: boolean,
}) => {
  const dispatch = useAppDispatch();
  const history = useHistory();
  const logsChecked = useAppSelector(selectLogsChecked);
  const { deleteLocalLog, updateLocalLog } = useLogs();
  const [isLoading, setIsLoading] = useState(true);
  const [isClosing, setIsClosing] = useState(false);
  const [preview, setPreview] = useState<CloseReviewedReportPreview | null>(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      const res = await dispatch(fetchCloseReviewedReportPreview({
        ...(activityLogId ? { activityLogId } : {}),
        ...(reportId ? { reportId } : {}),
      }));
      if (cancelled) return;
      if (res.status !== 'success' || !res.data || typeof res.data !== 'object') {
        toast(res.data || 'This report cannot be closed', { type: 'error' });
        onExit();
        return;
      }
      setPreview(res.data as CloseReviewedReportPreview);
      setIsLoading(false);
    })();
    return () => {
      cancelled = true;
    };
  }, [activityLogId, reportId, dispatch, onExit]);

  const onViewReport = () => {
    if (!preview?.report?.id) return;
    logButtonClick('activityLogs_view_report_before_close');
    onExit();
    history.push(ERP_PATHS.reportingTab(reportingPeekNavItem(preview.report.id)));
  };

  const onCloseForever = async () => {
    if (!preview?.report?.id) return;
    setIsClosing(true);
    logButtonClick('activityLogs_close_reviewed_report');
    const res = await dispatch(closeReviewedManagerReport(
      preview.report.id,
      activityLogId,
    ));
    if (res.status !== 'success') {
      setIsClosing(false);
      toast(res.data || 'Could not close this report', { type: 'error' });
      return;
    }
    const deletedIds: string[] = Array.isArray(res.data?.deletedLogIds)
      ? res.data.deletedLogIds.map((id: unknown) => String(id || '')).filter(Boolean)
      : [activityLogId];
    deletedIds.forEach((id) => deleteLocalLog(id));
    if (res.data?.activity) {
      updateLocalLog(res.data.activity as Log);
      if (!logsChecked.includes('Report closed')) {
        dispatch(setLogsChecked([...logsChecked, 'Report closed']));
      }
    }
    setIsClosing(false);
    toast('Report closed. User alerts were removed', { type: 'success' });
    onExit(true);
  };

  const otherCount = preview?.otherAlertCount || 0;
  const lostCount = (preview?.thisAlert ? 1 : 0) + otherCount;
  const reportSubject = String(preview?.report?.subject || '').trim() || 'this report';
  const subjectLabel = reportSubject === 'this report' ? reportSubject : `“${reportSubject}”`;
  const lostLabel = lostCount === 1
    ? '1 user alert'
    : lostCount > 1
      ? `${lostCount} user alerts`
      : 'the user alerts on this report';

  return (
    <>
      {(isLoading || isClosing) ? <Loader isSticky /> : (
        <S.WideContainer>
          <EscapeButton onClick={() => onExit()} />
          <S.Header>
            <S.DangerIcon aria-hidden>
              <HighlightOff fontSize="inherit" />
            </S.DangerIcon>
            <S.MainLabel>Close this report?</S.MainLabel>
            <S.SubLabel>
              Confirm that you want to close
              {' '}
              {subjectLabel}
              . This is not the same as filing or dismissing.
            </S.SubLabel>
          </S.Header>
          <S.SummaryLabel>What closing means</S.SummaryLabel>
          <S.MeaningList>
            <S.MeaningItem>
              {lostLabel}
              {' '}
              in Activity logs are deleted forever. There is no restore.
            </S.MeaningItem>
            <S.MeaningItem>
              Staff who sent and received the report can no longer open it.
            </S.MeaningItem>
            <S.MeaningItem>
              A Report closed log is added with your name. This cannot be undone.
            </S.MeaningItem>
          </S.MeaningList>
          {preview?.thisAlert ? (
            <S.LostList>
              <S.LostItem>
                <span>{preview.thisAlert.username}</span>
                <S.LostStanding>
                  {reportContextStandingLabel(preview.thisAlert.standing)}
                </S.LostStanding>
              </S.LostItem>
            </S.LostList>
          ) : null}
          {preview?.otherAlerts?.length ? (
            <>
              <S.SummaryLabel>
                {preview.thisAlert
                  ? 'Other user alerts that will be deleted'
                  : 'User alerts that will be deleted'}
              </S.SummaryLabel>
              <S.LostList>
                {preview.otherAlerts.map((alert) => (
                  <S.LostItem key={alert.logId || alert.username}>
                    <span>{alert.username}</span>
                    <S.LostStanding>
                      {reportContextStandingLabel(alert.standing)}
                    </S.LostStanding>
                  </S.LostItem>
                ))}
              </S.LostList>
            </>
          ) : null}
          {preview?.alertLevelSummary ? (
            <S.SummaryBox>
              <S.SummaryLabel>Alert level summary</S.SummaryLabel>
              <S.SummaryText>{preview.alertLevelSummary}</S.SummaryText>
            </S.SummaryBox>
          ) : null}
          <S.Warning>
            <InfoOutlined fontSize="inherit" />
            <span>
              {hideViewReport
                ? 'Review the report on this tab first if you still need the full details.'
                : 'Open the report first if you still need the full details.'}
            </span>
          </S.Warning>
          {!hideViewReport ? (
            <S.PurpleBtn type="button" onClick={onViewReport}>
              View full report
            </S.PurpleBtn>
          ) : null}
          <S.Actions>
            <S.SecondaryBtn type="button" onClick={() => onExit()}>
              Cancel
            </S.SecondaryBtn>
            <S.DangerBtn type="button" onClick={onCloseForever}>
              Close report
            </S.DangerBtn>
          </S.Actions>
        </S.WideContainer>
      )}
    </>
  );
};

export default CloseReviewedReport;
